import React, { useState, useEffect, useRef } from 'react';
import './MapSelector.css';
import { isWithinDeliveryArea, BUJUMBURA_CENTER } from '../../utils/locationUtils';

// Dynamic import for Leaflet to handle SSR and loading issues
let leafletComponents = null;
let L = null;

const loadLeaflet = async () => {
  try {
    const leafletModule = await import('leaflet');
    const reactLeafletModule = await import('react-leaflet');
    await import('leaflet/dist/leaflet.css');
    
    // Fix for default marker icons in Leaflet with webpack
    const iconModule = await import('leaflet/dist/images/marker-icon.png');
    const iconShadowModule = await import('leaflet/dist/images/marker-shadow.png');
    
    L = leafletModule.default;
    
    const DefaultIcon = L.icon({
      iconUrl: iconModule.default,
      shadowUrl: iconShadowModule.default,
      iconSize: [25, 41],
      iconAnchor: [12, 41]
    });
    
    L.Marker.prototype.options.icon = DefaultIcon;
    
    leafletComponents = {
      MapContainer: reactLeafletModule.MapContainer,
      TileLayer: reactLeafletModule.TileLayer,
      Marker: reactLeafletModule.Marker,
      useMapEvents: reactLeafletModule.useMapEvents,
      useMap: reactLeafletModule.useMap
    };
    
    return true;
  } catch (error) {
    console.error('Failed to load Leaflet:', error);
    return false;
  }
};

// Component to handle map events and provide map reference
function MapController({ mapRef, position }) {
  const map = leafletComponents?.useMap();
  
  useEffect(() => {
    if (mapRef && map) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  useEffect(() => {
    if (map && position && position.lat && position.lng) {
      map.setView([position.lat, position.lng], map.getZoom());
    }
  }, [map, position]);

  return null;
}

// Component to handle map click events
function LocationMarker({ position, setPosition, onPositionChange, onLocationValidation }) {
  const map = leafletComponents?.useMapEvents({
    click(e) {
      const newPosition = e.latlng;
      setPosition(newPosition);
      
      // Check if location is within Bujumbura Mairie
      const isValid = isWithinDeliveryArea(newPosition.lat, newPosition.lng);
      if (onLocationValidation) {
        onLocationValidation(isValid, newPosition);
      }
      
      if (onPositionChange) {
        onPositionChange(newPosition);
      }
      
      // Visual confirmation: animate to the new position
      map.flyTo(newPosition, Math.max(map.getZoom(), 15), {
        animate: true,
        duration: 1.5
      });
    },
  });

  if (!position || !leafletComponents) return null;

  return (
    <leafletComponents.Marker 
      position={position}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const newPosition = e.target.getLatLng();
          setPosition(newPosition);
          
          // Check if location is within Bujumbura Mairie
          const isValid = isWithinDeliveryArea(newPosition.lat, newPosition.lng);
          if (onLocationValidation) {
            onLocationValidation(isValid, newPosition);
          }
          
          if (onPositionChange) {
            onPositionChange(newPosition);
          }
        }
      }}
    />
  );
}

// Fallback map component using basic HTML and geolocation
const FallbackMap = ({ onLocationSelect, position, onPositionChange }) => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    setLocationError('');
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newLocation = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          };
          
          console.log('Got current location:', newLocation);
          
          if (onPositionChange) {
            onPositionChange(newLocation);
          }
          
          if (onLocationSelect) {
            onLocationSelect(newLocation);
          }
          
          setIsGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Unable to get your current location.');
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser.');
      setIsGettingLocation(false);
    }
  };

  return (
    <div className="fallback-map">
      <div className="fallback-controls">
        <button 
          type="button" 
          onClick={getCurrentLocation}
          disabled={isGettingLocation}
          className={`current-location-btn ${isGettingLocation ? 'loading' : ''}`}
        >
          {isGettingLocation ? (
            <>
              <span className="loading-spinner"></span>
              Getting your location...
            </>
          ) : (
            <>
              📍 Get My Current Location
            </>
          )}
        </button>
        
        {locationError && (
          <div className="location-error">
            <span className="error-icon">❌</span>
            <p>{locationError}</p>
          </div>
        )}
        
        {position && (
          <div className="selected-location">
            <div className="location-details">
              <strong>Current Location:</strong>
              <span className="coordinates">
                {position && position.lat && position.lng ? 
                  `${Number(position.lat).toFixed(6)}, ${Number(position.lng).toFixed(6)}` : 
                  'Location not available'
                }
              </span>
              <span className="validation-status valid">
                ✓ Location captured
              </span>
            </div>
          </div>
        )}
      </div>
      
      <div className="fallback-map-placeholder">
        <div className="map-placeholder-content">
          <h3>📍 Location Selection</h3>
          <p>Click the button above to use your current location for delivery.</p>
          <p><small>Map visualization temporarily unavailable - location detection still works!</small></p>
        </div>
      </div>
    </div>
  );
};

// Main map component
const MapSelector = ({ onLocationSelect, initialPosition = null }) => {
  const [position, setPosition] = useState(
    initialPosition && initialPosition.lat && initialPosition.lng ? 
    { lat: initialPosition.lat, lng: initialPosition.lng } : 
    { lat: BUJUMBURA_CENTER.lat, lng: BUJUMBURA_CENTER.lng }
  );
  const [isLocationValid, setIsLocationValid] = useState(true);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [mapError, setMapError] = useState('');
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const [leafletLoading, setLeafletLoading] = useState(true);
  const mapRef = useRef();

  // Load Leaflet on component mount
  useEffect(() => {
    const initLeaflet = async () => {
      console.log('Loading Leaflet...');
      const loaded = await loadLeaflet();
      setLeafletLoaded(loaded);
      setLeafletLoading(false);
      
      if (!loaded) {
        setMapError('Map could not be loaded. Using fallback location selector.');
      }
      
      console.log('Leaflet loading result:', loaded);
    };
    
    initLeaflet();
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('MapSelector initialized with position:', position);
    console.log('Initial position prop:', initialPosition);
  }, []);

  // Handle location validation
  const handleLocationValidation = (isValid, newPosition) => {
    setIsLocationValid(isValid);
    console.log('Location validation:', { isValid, position: newPosition });
  };

  // Get user's current location with visual confirmation
  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    setLocationError('');
    console.log('Getting current location...');
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          console.log('Got current location:', newLocation);
          
          // Check if location is within service area
          const isValid = isWithinDeliveryArea(newLocation.lat, newLocation.lng);
          
          setPosition(newLocation);
          setIsLocationValid(isValid);
          
          // Visual confirmation: fly to the location with animation
          if (mapRef.current) {
            mapRef.current.flyTo(newLocation, 16, {
              animate: true,
              duration: 2.0
            });
          }
          
          if (onLocationSelect) {
            onLocationSelect(newLocation);
          }
          
          setIsGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Unable to get your current location. Please try again or enable location access.');
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser.');
      setIsGettingLocation(false);
    }
  };

  // Handle position changes
  const handlePositionChange = (newPosition) => {
    console.log('Position changed to:', newPosition);
    setPosition(newPosition);
    if (onLocationSelect) {
      onLocationSelect(newPosition);
    }
  };

  // If Leaflet is still loading
  if (leafletLoading) {
    return (
      <div className="map-selector">
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  // If Leaflet failed to load, use fallback
  if (!leafletLoaded || mapError) {
    return (
      <FallbackMap 
        onLocationSelect={onLocationSelect}
        position={position}
        onPositionChange={handlePositionChange}
      />
    );
  }

  return (
    <div className="map-selector">
      <div className="map-controls">
        <button 
          type="button" 
          onClick={getCurrentLocation}
          disabled={isGettingLocation}
          className={`current-location-btn ${isGettingLocation ? 'loading' : ''}`}
        >
          {isGettingLocation ? (
            <>
              <span className="loading-spinner"></span>
              Getting your location...
            </>
          ) : (
            <>
              📍 Use My Current Location
            </>
          )}
        </button>
        
        <p className="map-instruction">
          Or click anywhere on the map to select your location
        </p>
      </div>

      {/* Location Error */}
      {locationError && (
        <div className="location-error">
          <span className="error-icon">❌</span>
          <p>{locationError}</p>
        </div>
      )}

      {/* Service Area Info */}
      <div className="service-area-info">
        <div className="info-content">
          <span className="info-icon">ℹ️</span>
          <span>Service area: Bujumbura Mairie</span>
        </div>
      </div>

      {/* Location Status Display */}
      {position && (
        <div className={`selected-location ${isLocationValid ? 'valid' : 'invalid'}`}>
          <div className="location-details">
            <strong>Selected Location:</strong>
            <span className="coordinates">
              {position && position.lat && position.lng ? 
                `${Number(position.lat).toFixed(6)}, ${Number(position.lng).toFixed(6)}` : 
                'Location not available'
              }
            </span>
            <span className={`validation-status ${isLocationValid ? 'valid' : 'invalid'}`}>
              {isLocationValid ? '✓ Valid delivery area' : '⚠️ Outside delivery area'}
            </span>
          </div>
        </div>
      )}

      <div className="map-container">
        <leafletComponents.MapContainer 
          center={position && position.lat && position.lng ? 
            [position.lat, position.lng] : 
            [BUJUMBURA_CENTER.lat, BUJUMBURA_CENTER.lng]
          }
          zoom={13} 
          style={{ height: '400px', width: '100%', zIndex: 1, position: 'relative' }}
          scrollWheelZoom={true}
          whenReady={() => console.log('Map is ready')}
        >
          <MapController mapRef={mapRef} position={position} />
          <leafletComponents.TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            eventHandlers={{
              tileerror: (e) => {
                console.error('Tile loading error:', e);
                setMapError('Map tiles failed to load');
              }
            }}
          />
          <LocationMarker 
            position={position} 
            setPosition={setPosition} 
            onPositionChange={handlePositionChange}
            onLocationValidation={handleLocationValidation}
          />
        </leafletComponents.MapContainer>
      </div>
    </div>
  );
};

export default MapSelector; 